import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getRoReportingGroups, markAsReportedToRo, type RoReportingGroup, type RoReportingQueryParams } from "../../api/roReporting";
import { useTelemetry } from "../../shared/useTelemetry";
import toast from "react-hot-toast";
import "./RoReportingModal.css";

interface RoReportingModalProps {
    campaignId?: string;
    onClose: () => void;
}

export const RoReportingModal = ({ campaignId, onClose }: RoReportingModalProps): JSX.Element => {
    const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
    const mouseDownOnOverlay = useRef(false);
    const filters: RoReportingQueryParams = {
        campaignId: campaignId || undefined,
        isReportedToRo: false
    };
    const queryClient = useQueryClient();
    const telemetry = useTelemetry();

    const { data: groups, isLoading, error } = useQuery({
        queryKey: ["ro-reporting", "modal", campaignId],
        queryFn: () => getRoReportingGroups(filters),
        enabled: !!campaignId,
        staleTime: 30 * 1000
    });

    const markAsReportedMutation = useMutation({
        mutationFn: markAsReportedToRo,
        onSuccess: () => {
            telemetry.track("ro-reporting.markedFromModal");
            toast.success("Leveranser markerade som rapporterade till RO");
            queryClient.invalidateQueries({ queryKey: ["ro-reporting"] });
            setSelectedGroups(new Set());
            onClose();
        },
        onError: (error: unknown) => {
            telemetry.trackError("ro-reporting.markFailed", error);
            const errorMessage = error instanceof Error ? error.message : "Kunde inte markera som rapporterade";
            toast.error(errorMessage);
        }
    });

    const handleToggleGroup = (groupKey: string) => {
        const newSelected = new Set(selectedGroups);
        if (newSelected.has(groupKey)) {
            newSelected.delete(groupKey);
        } else {
            newSelected.add(groupKey);
        }
        setSelectedGroups(newSelected);
    };

    const handleSelectAll = () => {
        if (!groups) return;
        const allKeys = groups.map(g => getGroupKey(g));
        setSelectedGroups(new Set(allKeys));
    };

    const handleDeselectAll = () => {
        setSelectedGroups(new Set());
    };

    const handleMarkAsReported = () => {
        if (selectedGroups.size === 0) {
            toast.error("Välj minst en grupp att markera");
            return;
        }

        const deliveryIds: string[] = [];
        groups?.forEach(group => {
            const groupKey = getGroupKey(group);
            if (selectedGroups.has(groupKey)) {
                deliveryIds.push(...group.deliveryIds);
            }
        });

        if (deliveryIds.length > 0) {
            markAsReportedMutation.mutate({ deliveryIds });
        }
    };

    const getGroupKey = (group: RoReportingGroup): string => {
        return `${group.clientRequestId}-${group.electoralDistrictId || "none"}`;
    };

    const unreportedGroups = groups?.filter(g => !g.isReportedToRo) ?? [];

    return (
        <div
            className="ro-reporting-modal-overlay"
            onMouseDown={(ev) => { mouseDownOnOverlay.current = ev.target === ev.currentTarget; }}
            onMouseUp={(ev) => {
                if (mouseDownOnOverlay.current && ev.target === ev.currentTarget) onClose();
                mouseDownOnOverlay.current = false;
            }}
        >
            <div className="ro-reporting-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ro-reporting-modal__header">
                    <h2 className="ro-reporting-modal__title">Markera som inskickade till RO</h2>
                    <button
                        type="button"
                        className="ro-reporting-modal__close"
                        onClick={onClose}
                        aria-label="Stäng"
                    >
                        ✕
                    </button>
                </div>

                <div className="ro-reporting-modal__body">
                    {isLoading && (
                        <div className="ro-reporting-modal__loading">Laddar leveranser...</div>
                    )}

                    {error && (
                        <div className="ro-reporting-modal__error">
                            Fel vid laddning: {(error as Error).message}
                        </div>
                    )}

                    {!isLoading && !error && unreportedGroups.length === 0 && (
                        <div className="ro-reporting-modal__empty">
                            Inga orapporterade leveranser hittades.
                        </div>
                    )}

                    {!isLoading && !error && unreportedGroups.length > 0 && (
                        <>
                            <div className="ro-reporting-modal__actions">
                                <button
                                    type="button"
                                    className="ro-reporting-modal__action-button"
                                    onClick={handleSelectAll}
                                >
                                    Välj alla
                                </button>
                                <button
                                    type="button"
                                    className="ro-reporting-modal__action-button"
                                    onClick={handleDeselectAll}
                                >
                                    Avmarkera alla
                                </button>
                                <div className="ro-reporting-modal__selection-count">
                                    {selectedGroups.size} av {unreportedGroups.length} valda
                                </div>
                            </div>

                            <div className="ro-reporting-modal__groups">
                                {unreportedGroups.map(group => {
                                    const groupKey = getGroupKey(group);
                                    return (
                                        <div
                                            key={groupKey}
                                            className={`ro-reporting-modal__group ${selectedGroups.has(groupKey) ? "selected" : ""}`}
                                            onClick={() => handleToggleGroup(groupKey)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedGroups.has(groupKey)}
                                                onChange={() => handleToggleGroup(groupKey)}
                                                className="ro-reporting-modal__checkbox"
                                            />
                                            <div className="ro-reporting-modal__group-content">
                                                <div className="ro-reporting-modal__group-header">
                                                    <span className="ro-reporting-modal__group-session">
                                                        Session: {group.clientRequestId.substring(0, 8)}...
                                                    </span>
                                                    <span className="ro-reporting-modal__group-district">
                                                        {group.electoralDistrictName || group.electoralDistrictCode || "Okänt valdistrikt"}
                                                    </span>
                                                </div>
                                                <div className="ro-reporting-modal__group-details">
                                                    <span>{group.deliveryCount} leveranser</span>
                                                    <span>•</span>
                                                    <span>{group.addressCount} adresser</span>
                                                    <span>•</span>
                                                    <span>{group.totalQuantity} flygblad</span>
                                                    <span>•</span>
                                                    <span>{new Date(group.lastDeliveredAt).toLocaleDateString("sv-SE")}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                <div className="ro-reporting-modal__footer">
                    <button
                        type="button"
                        className="ro-reporting-modal__footer-button ro-reporting-modal__footer-button--cancel"
                        onClick={onClose}
                    >
                        Avbryt
                    </button>
                    <button
                        type="button"
                        className="ro-reporting-modal__footer-button ro-reporting-modal__footer-button--confirm"
                        onClick={handleMarkAsReported}
                        disabled={selectedGroups.size === 0 || markAsReportedMutation.isPending}
                    >
                        {markAsReportedMutation.isPending
                            ? "Markerar..."
                            : `Markera som rapporterade (${selectedGroups.size})`}
                    </button>
                </div>
            </div>
        </div>
    );
};
